import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { config } from '@/config/config';
import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import {
  getAuthFailureReason,
  isInvalidTrialOfferError,
  trialOfferFailureMessage,
  trialOfferParamKey,
} from '@/modules/auth/constants';
import {
  clearLinkedInSigninState,
  completeLinkedInLoginFromCallback,
  LinkedInSsoError,
  readLinkedInIntent,
} from '@/modules/auth/linkedinSso';
import { useExternalLoginMutation } from '@/modules/auth/mutations/useExternalLoginMutation';
import {
  type AuthIntent,
  authProviders,
} from '@/modules/auth/types/externalLogin';
import { getRoleBasedReturnTo } from '@/modules/auth/utils/getRoleBasedReturnTo';

import {
  type LinkedInCallbackError,
  LinkedInCallbackStatus,
} from './linkedInCallbackStatus/LinkedInCallbackStatus';

const getLinkedInSsoErrorCode = (error: unknown) => {
  if (error instanceof LinkedInSsoError) {
    return error.code;
  }

  return 'exchange_failed';
};

const getLinkedInSsoErrorMessage = (error: unknown) => {
  if (error instanceof LinkedInSsoError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'LinkedIn sign-in failed. Please try again.';
};

export const LinkedInCallback = () => {
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const externalLoginMutation = useExternalLoginMutation();
  const hasStarted = useRef(false);

  const [failure, setFailure] = useState<LinkedInCallbackError>();

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;

    const completeSignIn = async () => {
      trackEvent('linkedInSSO', {
        stage: 'callback_received',
      });

      let trialOfferCode: string | undefined;
      // Set once the callback is validated; a failure before that reads it
      // from the stored state instead.
      let intent: AuthIntent | undefined;
      try {
        const callbackResult = await completeLinkedInLoginFromCallback(
          window.location.href,
        );
        intent = callbackResult.intent;

        const returnTo = new URL(
          callbackResult.returnTo,
          window.location.origin,
        );
        // LinkedIn's OAuth redirect only preserves data through the signed
        // OIDC state (see linkedinSso.ts), so the offer code rides in
        // `returnTo`'s query string instead of its own field. Strip it here
        // for the same reason SignUp.tsx strips it before navigating there:
        // keep the one-time code out of app URLs.
        trialOfferCode =
          returnTo.searchParams.get(trialOfferParamKey) ?? undefined;
        returnTo.searchParams.delete(trialOfferParamKey);

        await externalLoginMutation.mutateAsync({
          provider: authProviders.linkedIn,
          authorizationCode: callbackResult.code,
          redirectUri: config.external.linkedinRedirectUri,
          idToken: null,
          trialOfferCode,
          acceptTerms: callbackResult.acceptTerms,
        });

        trackEvent('linkedInSSO', {
          stage: 'exchange_succeeded',
        });
        trackEvent(
          callbackResult.intent === 'signUp' ? 'userSignedUp' : 'userSignedIn',
          { authMethod: 'LinkedIn' },
        );

        const cleanReturnTo = `${returnTo.pathname}${returnTo.search}${returnTo.hash}`;
        const redirectPath = await getRoleBasedReturnTo(cleanReturnTo);
        navigate(redirectPath, { replace: true });
      } catch (error) {
        const failedIntent =
          intent ?? (await readLinkedInIntent(window.location.href));
        await clearLinkedInSigninState();

        trackEvent('linkedInSSO', {
          stage: 'exchange_failed',
          errorCode: getLinkedInSsoErrorCode(error),
        });
        // Without the stored state (e.g. the callback opened in another
        // browser) only the linkedInSSO event above records the failure.
        if (failedIntent) {
          trackEvent(
            failedIntent === 'signUp' ? 'signUpFailed' : 'signInFailed',
            {
              authMethod: 'LinkedIn',
              reason:
                error instanceof LinkedInSsoError
                  ? 'linkedin_sso_error'
                  : getAuthFailureReason(error, 'LinkedIn'),
            },
          );
        }

        const invalidTrialOffer =
          trialOfferCode && isInvalidTrialOfferError(error);

        setFailure({
          message: invalidTrialOffer
            ? trialOfferFailureMessage
            : getLinkedInSsoErrorMessage(error),
          signUpPath:
            trialOfferCode && !invalidTrialOffer
              ? `/sign-up?${new URLSearchParams({ [trialOfferParamKey]: trialOfferCode })}`
              : '/sign-up',
        });
      }
    };

    completeSignIn();
  }, [externalLoginMutation, navigate, trackEvent]);

  return <LinkedInCallbackStatus error={failure} />;
};
